/**
 * OS notifications. Desktop: native (Windows toast, KDE/freedesktop) via the
 * Tauri plugin. Browser: through the service worker when possible, which adds
 * "Afvinken"/"Uitstellen" buttons (Chromium), else the plain Notification API.
 */
import { isTauri } from '@nexus/store/tauri';
import { useUi } from '../state/ui';

export type Permission = 'granted' | 'denied' | 'default' | 'unsupported';

export interface SystemNotice {
  id: string;
  title: string;
  body: string;
  /** Notifications with the same tag replace each other (local reminder and its push). */
  tag?: string;
  /** Labels for the action buttons (browser only). */
  actions?: { done: string; snooze: string };
}

export async function permission(): Promise<Permission> {
  if (isTauri()) {
    const { isPermissionGranted } = await import('@tauri-apps/plugin-notification');
    return (await isPermissionGranted()) ? 'granted' : 'default';
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestPermission(): Promise<Permission> {
  if (isTauri()) {
    const { requestPermission: request } = await import('@tauri-apps/plugin-notification');
    return (await request()) === 'granted' ? 'granted' : 'denied';
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.requestPermission();
}

export async function showSystemNotification(n: SystemNotice, onClick: () => void): Promise<void> {
  if ((await permission()) !== 'granted') return;
  if (isTauri()) {
    const { sendNotification } = await import('@tauri-apps/plugin-notification');
    sendNotification({ title: n.title, body: n.body });
    return;
  }
  const registration = await navigator.serviceWorker?.getRegistration?.().catch(() => undefined);
  if (registration) {
    await registration.showNotification(n.title, {
      body: n.body,
      tag: n.tag ?? n.id,
      icon: '/icon.svg',
      data: { id: n.id },
      ...(n.actions
        ? {
            actions: [
              { action: 'done', title: n.actions.done },
              { action: 'snooze', title: n.actions.snooze },
            ],
          }
        : {}),
    } as NotificationOptions);
    return;
  }
  const plain = new Notification(n.title, { body: n.body, tag: n.tag ?? n.id, icon: '/icon.svg' });
  plain.onclick = () => {
    window.focus();
    onClick();
    plain.close();
  };
}

export interface NotificationAction {
  id: string;
  action: 'open' | 'done' | 'snooze';
}

/** Clicks on browser notifications arrive from the service worker. */
export function onNotificationAction(handler: (a: NotificationAction) => void): () => void {
  const sw = navigator.serviceWorker;
  if (!sw) return () => {};
  const listener = (e: MessageEvent<{ type?: string } & NotificationAction>) => {
    if (e.data?.type === 'nexus-notification') handler({ id: e.data.id, action: e.data.action });
  };
  sw.addEventListener('message', listener);
  return () => sw.removeEventListener('message', listener);
}

/** Clicks on pushed reminders (sent by the cloud) while the app is open. */
export function onPushReminderClick(
  handler: (r: { pageId: string; taskId: string | null }) => void,
): () => void {
  const sw = navigator.serviceWorker;
  if (!sw) return () => {};
  const listener = (e: MessageEvent<{ type?: string; pageId?: string; taskId?: string }>) => {
    if (e.data?.type === 'nexus-reminder') {
      if (e.data.pageId) handler({ pageId: e.data.pageId, taskId: e.data.taskId ?? null });
      else useUi.getState().set({ notificationsOpen: true });
    }
  };
  sw.addEventListener('message', listener);
  return () => sw.removeEventListener('message', listener);
}
